import { PageError } from '@ctw/shared/components/errors/page-error';
import { ZapProvider } from '@ctw/shared/context/zap-context';
import { isZapVersion, ZuiProvider } from '@ctw/shared/context/zui-provider';
import { trimStart } from 'lodash-es';
import { useMemo } from 'react';
import { BrowserRouter, Route, Routes, useLocation, useNavigate } from 'react-router';
import { SalesforceV1 } from './zaps/salesforce-v1';
import { VimV1 } from './zaps/vim-v1';
import { ZapV1 } from './zaps/zap-v1';
import { ZapV2 } from './zaps/zap-v2';

const RouteDefinitions = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // IMPORTANT! This logic to determine the correct navigation path is also found in url.ts in the pathWithParameters function.
  // If you are making updates you may need to update both places.

  // biome-ignore lint/correctness/useExhaustiveDependencies: No explanation was provided. Please be a good citizen and add one!
  const zapVersion = useMemo(() => {
    if (location.pathname === '/') {
      const url = new URL(window.location.href);
      url.pathname = '/v1';
      window.location.href = url.toString();
      return '';
    }

    const maybeZapVersion = trimStart(location.pathname, '/').split('/')[0];
    return isZapVersion(maybeZapVersion) ? maybeZapVersion : 'v1';
  }, []);

  const navigateBasePath = useMemo(() => {
    if (window.location.pathname.startsWith('/salesforce')) {
      return '/salesforce/v1';
    }
    if (window.location.pathname.startsWith('/vim')) {
      return '/vim/v1';
    }
    return `/${zapVersion}`;
  }, [zapVersion]);

  return (
    <ZuiProvider
      service="zap"
      navigate={navigate}
      navigateBasePath={navigateBasePath}
      navigatePersistentQueryParams={['config']}
      pathname={useLocation}
    >
      <ZapProvider>
        <Routes>
          <Route path="/v1">
            <Route index={true} path="*" element={<ZapV1 />} />
          </Route>
          <Route path="/v2">
            <Route index={true} path="*" element={<ZapV2 />} />
          </Route>
          <Route path="/salesforce/v1">
            <Route index={true} path="*" element={<SalesforceV1 />} />
          </Route>
          <Route path="/vim/v1">
            <Route index={true} path="*" element={<VimV1 />} />
          </Route>
          <Route path="/health" element={<>OK</>} />
        </Routes>
      </ZapProvider>
    </ZuiProvider>
  );
};

interface AppProps {
  isInsideIFrame: boolean;
}

export function App({ isInsideIFrame }: AppProps) {
  if (!isInsideIFrame) {
    return (
      <PageError
        title="Proxy Authentication Required"
        message="This page will only authenticate and load when embedded inside an iframe. For assistance, get in contact with your representative at Zus Health."
        errorCode="HTTP ERROR 407"
        supportEmailSubject="Help Embedding Zus Aggregated Profile"
      />
    );
  }
  return (
    <BrowserRouter>
      <RouteDefinitions />
    </BrowserRouter>
  );
}
